import { financeNumber } from '../pendingInvoiceCost';
import api from '../../../services/api';

export type CompanyBalanceReviewState =
  | 'INCLUDED'
  | 'PARTIAL'
  | 'READY'
  | 'REVIEW'
  | 'PLANNED'
  | 'EXCLUDED';

export type CompanyBalanceReviewReason =
  | 'RESULT_REQUIRES_UPDATE'
  | 'SETTLEMENT_REQUIRES_ADMIN'
  | 'MISSING_REVENUE'
  | 'MISSING_COSTS'
  | 'NEGATIVE_MARGIN'
  | 'HIGH_MARGIN'
  | 'NO_PRODUCTS'
  | 'MANUAL_PRODUCTS'
  | 'NO_DELIVERY_EVIDENCE'
  | 'NO_OPERATIONAL_LINKS'
  | 'CARGO_INCOMPLETE'
  | 'TRANSPORT_INCOMPLETE'
  | 'WZ_NOT_RELEASED'
  | 'FINANCE_TOTAL_MISMATCH'
  | 'RECOGNIZED_EXCEEDS_CURRENT';

export interface CompanyBalanceMoney {
  revenue: number;
  totalCost: number | null;
  margin: number | null;
  marginPercent: number | null;
}

export interface CompanyBalanceEvidence {
  productCount: number;
  catalogProductCount: number;
  supplierOrderCount: number;
  receivedSupplierOrderCount: number;
  transportCount: number;
  deliveredTransportCount: number;
  cargoItemCount: number;
  deliveredCargoProductCount: number;
  pzCount: number;
  receivedPzCount: number;
  wzCount: number;
  releasedWzCount: number;
  orderedLineValue: number;
  deliveredLineValue: number;
  deliveryPercent: number;
}

export interface CompanyBalanceOrder {
  id: string;
  orderNumber: string;
  displayOrderNumber: string;
  customer: { id: string | null; name: string } | null;
  accountManagerName: string | null;
  status: string;
  orderDate: string | null;
  plannedDate: string | null;
  currency: string;
  isSettled: boolean;
  settledAt: string | null;
  settlementEntryCount: number;
  periodSettlementEntryCount: number;
  lastRecognizedAt: string | null;
  settlementStatus: 'OPEN' | 'PARTIALLY_SETTLED' | 'SETTLED';
  current: CompanyBalanceMoney;
  recognized: CompanyBalanceMoney;
  periodRecognized: CompanyBalanceMoney;
  remaining: CompanyBalanceMoney;
  evidence: CompanyBalanceEvidence;
  reviewState: CompanyBalanceReviewState;
  requiresReview: boolean;
  reviewReasons: CompanyBalanceReviewReason[];
}

export interface CompanyBalanceMonth {
  month: string;
  orderCount: number;
  revenue: number;
  orderCosts: number | null;
  orderMargin: number | null;
  marginPercent: number | null;
  fixedCosts: {
    total: number;
    byCategory: Record<string, number>;
  };
  forecastFixedCosts: {
    total: number;
    byCategory: Record<string, number>;
  };
  netResult: number | null;
  planned: {
    month: string;
    orderCount: number;
    revenue: number;
    totalCost: number | null;
    margin: number | null;
    marginPercent: number | null;
    reviewOrderCount: number;
  };
}

export interface CompanyBalanceResponse {
  from: string;
  to: string;
  summary: {
    recognized: {
      orderCount: number;
      revenue: number;
      totalCost: number | null;
      margin: number | null;
      marginPercent: number | null;
      fixedCosts: number;
      netResult: number | null;
    };
    planned: {
      orderCount: number;
      revenue: number;
      totalCost: number | null;
      margin: number | null;
      marginPercent: number | null;
      reviewOrderCount: number;
      fixedCosts: number;
      netResult: number | null;
    };
    queue: {
      included: number;
      partiallyIncluded: number;
      ready: number;
      review: number;
      planned: number;
    };
    orders: {
      orderCount: number;
      averageRealizationPercent: number | null;
      revenue: number;
      totalCost: number | null;
      marginPercent: number | null;
    };
  };
  months: CompanyBalanceMonth[];
  orders: CompanyBalanceOrder[];
}

function record(value: unknown): Record<string, any> {
  return value && typeof value === 'object' ? value as Record<string, any> : {};
}

function num(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function text(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function bool(value: unknown): boolean {
  return value === true;
}

function nullableText(value: unknown): string | null {
  return typeof value === 'string' && value ? value : null;
}

function normalizeMoney(value: unknown): CompanyBalanceMoney {
  const source = record(value);
  return {
    revenue: num(source.revenue),
    totalCost: financeNumber(source.totalCost),
    margin: financeNumber(source.margin),
    marginPercent: financeNumber(source.marginPercent),
  };
}

function normalizeEvidence(value: unknown): CompanyBalanceEvidence {
  const source = record(value);
  return {
    productCount: num(source.productCount),
    catalogProductCount: num(source.catalogProductCount),
    supplierOrderCount: num(source.supplierOrderCount),
    receivedSupplierOrderCount: num(source.receivedSupplierOrderCount),
    transportCount: num(source.transportCount),
    deliveredTransportCount: num(source.deliveredTransportCount),
    cargoItemCount: num(source.cargoItemCount),
    deliveredCargoProductCount: num(source.deliveredCargoProductCount),
    pzCount: num(source.pzCount),
    receivedPzCount: num(source.receivedPzCount),
    wzCount: num(source.wzCount),
    releasedWzCount: num(source.releasedWzCount),
    orderedLineValue: num(source.orderedLineValue),
    deliveredLineValue: num(source.deliveredLineValue),
    deliveryPercent: num(source.deliveryPercent),
  };
}

function normalizeOrder(value: unknown): CompanyBalanceOrder {
  const source = record(value);
  const customer = record(source.customer);
  const reviewState = text(source.reviewState, 'REVIEW') as CompanyBalanceReviewState;
  const settlementStatus = text(source.settlementStatus, 'OPEN') as CompanyBalanceOrder['settlementStatus'];

  return {
    id: text(source.id),
    orderNumber: text(source.orderNumber),
    displayOrderNumber: text(source.displayOrderNumber, text(source.orderNumber)),
    customer: source.customer ? {
      id: nullableText(customer.id),
      name: text(customer.name, '—'),
    } : null,
    accountManagerName: nullableText(source.accountManagerName),
    status: text(source.status),
    orderDate: nullableText(source.orderDate),
    plannedDate: nullableText(source.plannedDate),
    currency: text(source.currency, 'PLN'),
    isSettled: bool(source.isSettled),
    settledAt: nullableText(source.settledAt),
    settlementEntryCount: num(source.settlementEntryCount),
    periodSettlementEntryCount: num(source.periodSettlementEntryCount),
    lastRecognizedAt: nullableText(source.lastRecognizedAt),
    settlementStatus,
    current: normalizeMoney(source.current),
    recognized: normalizeMoney(source.recognized),
    periodRecognized: normalizeMoney(source.periodRecognized),
    remaining: normalizeMoney(source.remaining),
    evidence: normalizeEvidence(source.evidence),
    reviewState,
    requiresReview: bool(source.requiresReview),
    reviewReasons: Array.isArray(source.reviewReasons)
      ? source.reviewReasons.map((reason: unknown) => text(reason)).filter(Boolean) as CompanyBalanceReviewReason[]
      : [],
  };
}

function normalizeMonth(value: unknown): CompanyBalanceMonth {
  const source = record(value);
  const fixedCosts = record(source.fixedCosts);
  const forecastFixedCosts = record(source.forecastFixedCosts);
  const planned = record(source.planned);
  const byCategory = record(fixedCosts.byCategory);
  const forecastByCategory = record(forecastFixedCosts.byCategory);

  return {
    month: text(source.month),
    orderCount: num(source.orderCount),
    revenue: num(source.revenue),
    orderCosts: financeNumber(source.orderCosts),
    orderMargin: financeNumber(source.orderMargin),
    marginPercent: financeNumber(source.marginPercent),
    fixedCosts: {
      total: num(fixedCosts.total),
      byCategory: Object.fromEntries(Object.entries(byCategory).map(([key, amount]) => [key, num(amount)])),
    },
    forecastFixedCosts: {
      total: num(forecastFixedCosts.total),
      byCategory: Object.fromEntries(
        Object.entries(forecastByCategory).map(([key, amount]) => [key, num(amount)]),
      ),
    },
    netResult: financeNumber(source.netResult),
    planned: {
      month: text(planned.month, text(source.month)),
      orderCount: num(planned.orderCount),
      revenue: num(planned.revenue),
      totalCost: financeNumber(planned.totalCost),
      margin: financeNumber(planned.margin),
      marginPercent: financeNumber(planned.marginPercent),
      reviewOrderCount: num(planned.reviewOrderCount),
    },
  };
}

export function normalizeCompanyBalance(value: unknown): CompanyBalanceResponse {
  const source = record(value);
  const summary = record(source.summary);
  const recognized = record(summary.recognized);
  const planned = record(summary.planned);
  const queue = record(summary.queue);
  const ordersSummary = record(summary.orders);

  return {
    from: text(source.from),
    to: text(source.to),
    summary: {
      recognized: {
        orderCount: num(recognized.orderCount),
        revenue: num(recognized.revenue),
        totalCost: financeNumber(recognized.totalCost),
        margin: financeNumber(recognized.margin),
        marginPercent: financeNumber(recognized.marginPercent),
        fixedCosts: num(recognized.fixedCosts),
        netResult: financeNumber(recognized.netResult),
      },
      planned: {
        orderCount: num(planned.orderCount),
        revenue: num(planned.revenue),
        totalCost: financeNumber(planned.totalCost),
        margin: financeNumber(planned.margin),
        marginPercent: financeNumber(planned.marginPercent),
        reviewOrderCount: num(planned.reviewOrderCount),
        fixedCosts: num(planned.fixedCosts),
        netResult: financeNumber(planned.netResult),
      },
      queue: {
        included: num(queue.included),
        partiallyIncluded: num(queue.partiallyIncluded),
        ready: num(queue.ready),
        review: num(queue.review),
        planned: num(queue.planned),
      },
      orders: {
        orderCount: num(ordersSummary.orderCount),
        averageRealizationPercent: ordersSummary.averageRealizationPercent === null
          || ordersSummary.averageRealizationPercent === undefined
          ? null
          : num(ordersSummary.averageRealizationPercent),
        revenue: num(ordersSummary.revenue),
        totalCost: financeNumber(ordersSummary.totalCost),
        marginPercent: financeNumber(ordersSummary.marginPercent),
      },
    },
    months: Array.isArray(source.months) ? source.months.map(normalizeMonth) : [],
    orders: Array.isArray(source.orders) ? source.orders.map(normalizeOrder) : [],
  };
}

export async function fetchCompanyBalance(from: string, to: string): Promise<CompanyBalanceResponse> {
  const response = await api.get('/reports/company-balance', { params: { from, to } });
  return normalizeCompanyBalance(response.data);
}
